/**
 * @fileoverview Empty-state chooser shown on the template canvas when no forms
 * are attached yet.
 * @module template-builder
 *
 * Responsibilities:
 * - Offer the four first-form entry points: blank form, library pick, header
 *   form, system module.
 *
 * Dependencies:
 * - lucide-react icons.
 *
 * Architecture:
 * - Presentation-only; delegates the choice to the host canvas via onChoose.
 *
 * Notes:
 * - Every choice is a create-then-attach path on hosts that fork library
 *   forms, so all buttons disable together when canCreateForms is false.
 */
import { Cpu, FileText, FolderOpen, Plus } from 'lucide-react';

export type FirstFormChoice = 'new' | 'library' | 'header' | 'module';

export function CanvasEmptyState({
  canAddModule = true,
  canCreateForms = true,
  onChoose,
}: {
  canAddModule?: boolean;
  /** When false every choice is disabled — each one creates a form record. */
  canCreateForms?: boolean;
  onChoose: (choice: FirstFormChoice) => void;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-sm w-full space-y-3">
        <p className="text-center text-sm font-medium text-gray-500 mb-4">Add your first form to get started</p>
        <button
          onClick={() => onChoose('new')}
          disabled={!canCreateForms}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 text-blue-600 shrink-0"><Plus size={16} /></span>
          <div>
            <div className="text-sm font-medium text-gray-900">New Form</div>
            <div className="text-xs text-gray-400 mt-0.5">Start with a blank form</div>
          </div>
        </button>
        <button
          onClick={() => onChoose('library')}
          disabled={!canCreateForms}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span className="flex items-center justify-center w-8 h-8 rounded-md bg-violet-50 text-violet-600 shrink-0"><FolderOpen size={16} /></span>
          <div>
            <div className="text-sm font-medium text-gray-900">Form Library</div>
            <div className="text-xs text-gray-400 mt-0.5">Pick from your existing forms</div>
          </div>
        </button>
        <button
          onClick={() => onChoose('header')}
          disabled={!canCreateForms}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 hover:bg-amber-50 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span className="flex items-center justify-center w-8 h-8 rounded-md bg-amber-50 text-amber-600 shrink-0"><FileText size={16} /></span>
          <div>
            <div className="text-sm font-medium text-gray-900">Header / Instructions</div>
            <div className="text-xs text-gray-400 mt-0.5">Instruction page shown before the first form</div>
          </div>
        </button>
        {canAddModule && (
          <button
            onClick={() => onChoose('module')}
            disabled={!canCreateForms}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-teal-200 hover:bg-teal-50 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-md bg-teal-50 text-teal-600 shrink-0"><Cpu size={16} /></span>
            <div>
              <div className="text-sm font-medium text-gray-900">System Module</div>
              <div className="text-xs text-gray-400 mt-0.5">Add Contacts or Additional Violations</div>
            </div>
          </button>
        )}
        {!canCreateForms && (
          <p className="text-center text-xs text-gray-400 pt-1">You don&apos;t have permission to add forms to this template.</p>
        )}
      </div>
    </div>
  );
}
